/**
 * TypeStep — step 1 of the add-account flow: pick a warehouse type. Selecting a
 * row advances to the warehouse-selection step (no separate Next button).
 */
import { ChevronRight } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/_shared/components/ui/button';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';

import { SUPPORTED_WAREHOUSE_TYPES, type WarehouseTypeMeta } from '../warehouseTypes';

interface TypeStepProps {
  onSelect: (meta: WarehouseTypeMeta) => void;
}

export function TypeStep({ onSelect }: TypeStepProps): React.JSX.Element {
  const { isClickHouseWarehouseEnabled, platformAccountCreateFamilies } = useWorkspace();
  // ClickHouse has one create path, the platform account form: offer it only where that form is offered.
  const clickHouseOffered =
    isClickHouseWarehouseEnabled && platformAccountCreateFamilies.includes('CLICKHOUSE:password');
  const types = SUPPORTED_WAREHOUSE_TYPES.filter((meta) => meta.key !== 'CLICKHOUSE' || clickHouseOffered);

  return (
    <div className="grid gap-1">
      {types.map((meta) => {
        const { Icon } = meta;
        return (
          <Button
            key={meta.key}
            type="button"
            variant="tertiary"
            onClick={() => onSelect(meta)}
            Icon={<Icon className="size-6 shrink-0" />}
            suffixIcon={
              <ChevronRight className="size-4 shrink-0 text-grey-400 opacity-0 transition-opacity group-hover:opacity-100" />
            }
            className="group h-auto w-full justify-start gap-3 px-2 py-2 font-normal hover:bg-grey-50"
          >
            <span className="min-w-0 flex-1 truncate text-left text-sm text-foreground">{meta.label}</span>
          </Button>
        );
      })}
    </div>
  );
}
